import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';

import { BackendRouteMetric, BackendRoutePerformanceResponse, ObservabilityRangePreset, ObservabilityRankingName } from '../../models/observability/backend-route-performance.model';
import { ObservabilityService } from '../../services/observability/observability.service';

@Component({ selector: 'app-observability', templateUrl: './observability.component.html', styleUrls: ['./observability.component.scss'] })
export class ObservabilityComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort?: MatSort;
  readonly ranges: Array<{ value: ObservabilityRangePreset; label: string }> = [{ value: '1h', label: 'Last hour' }, { value: '24h', label: 'Last 24 hours' }, { value: '7d', label: 'Last 7 days' }];
  readonly rankingOptions: Array<{ value: ObservabilityRankingName; label: string }> = [{ value: 'topByTotalDuration', label: 'Total backend time' }, { value: 'topByVolume', label: 'Request volume' }, { value: 'topByP95', label: 'p95 latency' }];
  readonly columns = ['rank', 'route', 'estimatedRequests', 'sampleCount', 'totalDurationMs', 'averageMs', 'p50Ms', 'p95Ms', 'p99Ms', 'failureRate'];
  readonly dataSource = new MatTableDataSource<BackendRouteMetric>([]);
  readonly filterControl = new FormControl('');
  range: ObservabilityRangePreset = '24h';
  ranking: ObservabilityRankingName = 'topByTotalDuration';
  response?: BackendRoutePerformanceResponse;
  loading = false;
  errorMessage = '';

  constructor(private readonly observabilityService: ObservabilityService) {
    this.dataSource.filterPredicate = (row, filter) => row.route.toLowerCase().includes(filter);
    this.dataSource.sortingDataAccessor = (row, column) => (row as any)[column] ?? Number.NEGATIVE_INFINITY;
  }

  ngOnInit(): void {
    this.filterControl.valueChanges.subscribe((value) => this.dataSource.filter = String(value || '').trim().toLowerCase());
    this.load();
  }

  ngAfterViewInit(): void { if (this.sort) this.dataSource.sort = this.sort; }

  load(): void {
    if (this.loading) return;
    this.loading = true;
    this.errorMessage = '';
    this.observabilityService.getBackendRoutePerformance(this.range).then((response) => {
      this.response = response;
      this.ensureWatchedOption();
      this.selectRows();
    }).catch(() => {
      this.errorMessage = 'Observability data is temporarily unavailable. Please try again.';
      this.dataSource.data = [];
    }).finally(() => this.loading = false);
  }

  onRangeChange(): void { this.load(); }
  onRankingChange(): void { this.selectRows(); }

  formatDuration(value: number | null): string {
    if (value == null) return 'N/A';
    return value >= 1000 ? `${(value / 1000).toFixed(2)} s` : `${value.toFixed(1)} ms`;
  }

  formatTotalDuration(value: number | null): string {
    if (value == null) return 'N/A';
    if (value >= 60_000) return `${(value / 60_000).toFixed(1)} min`;
    return this.formatDuration(value);
  }

  private selectRows(): void {
    if (!this.response) return;
    this.dataSource.data = this.ranking === 'watchedRoutes' ? this.response.watchedRoutes : this.response.rankings[this.ranking];
  }

  private ensureWatchedOption(): void {
    const index = this.rankingOptions.findIndex((option) => option.value === 'watchedRoutes');
    if (this.response?.watchedRoutes.length && index < 0) this.rankingOptions.push({ value: 'watchedRoutes', label: 'Watched routes' });
    if (!this.response?.watchedRoutes.length && index >= 0) this.rankingOptions.splice(index, 1);
  }
}
